import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, AlertCircle, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      toast.success('Password reset instructions sent!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to process request. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="noise-overlay" />
        
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img 
              src={NBR_LOGO} 
              alt="National Business Register" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
              <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
            </div>
          </div>

          {/* Success Card */}
          <div className="swiss-card p-8 text-center" data-testid="forgot-password-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Check Your Email</h2>
            <p className="text-slate-500 text-sm mb-6">
              If an account exists with the email <strong>{email}</strong>, you will receive 
              password reset instructions shortly.
            </p>
            <p className="text-slate-400 text-xs mb-6">
              The reset link will expire in 1 hour. If you don't see the email, check your spam folder.
            </p>
            <Link to="/login">
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">MARK</h1>
            <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
          </div>
        </div>

        {/* Forgot Password Card */}
        <div className="swiss-card p-8" data-testid="forgot-password-card">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-2">Forgot Password?</h2>
          <p className="text-slate-500 text-sm mb-6">
            Enter your email address and we'll send you instructions to reset your password.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2" data-testid="forgot-password-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                data-testid="forgot-password-email-input"
                className="h-11"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Reset Instructions
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          UK Trademark Monitoring System
        </p>
      </div>
    </div>
  );
};
